import Raven from 'raven-js'
import * as log from 'loglevel'

import store from '@/store'
import { AUTH_EXP_CHECK_INTERVAL, AUTH_LS_USER_KEY } from './auth.constants'

/**
 * Singleton class for managing login
 * session expiration timers.
 */
class LoginSessionManager {
  constructor() {
    // used with setInterval()
    this.interval = null
    this.delay = AUTH_EXP_CHECK_INTERVAL
  }

  /**
   * Register and watch new login session
   * If one exists, it will be cleared and replaced
   * @param {Number} iat Initialization time (milliseconds since epoch)
   * @param {Number} exp Expiration time (milleseconds since epoch)
   */
  set(iat, exp) {
    if (!iat || !exp) {
      throw new TypeError('Cannot call set() without iat and exp')
    }
    // Clear existing intervals
    if (this.interval) {
      log.warn('A session already exists. Avoid calling set() when other timers are running')
      clearInterval(this.interval)
    }
    this.interval = setInterval(() => {
      log.debug('loginSessionManager checking expiration')
      store.dispatch('Auth/checkExpiration')
    }, this.delay)
    log.debug('loginSessionManager started')
    // Should be registered with every session
    Raven.captureBreadcrumb({
      message: 'Registered a login session watcher',
      category: 'auth',
      data: {
        iat,
        exp,
      },
    })
  }

  /**
   * Clear existing watcher
   */
  clear() {
    if (!this.interval) {
      log.warn('Cannot clear login session: no interval exists in LoginSessionManager')
    }
    clearInterval(this.interval)
    log.debug('loginSessionManager stopped')
  }
}

/**
 * An abstract utility class for managing localStorage.
 * Methods are stateful due to localStorage state,
 * hence use of class instead of individual functions.
 */
export class LocalStorageHelper {
  constructor() {
    // TODO: Test this
    // Enforce class as final abstract
    if (new.target === LocalStorageHelper) {
      throw new TypeError('Cannot instantiate from abstract class LocalStorageHelper')
    }
  }
  /**
   * Remove user from localStorage
   */
  static clearUser() {
    localStorage.removeItem(AUTH_LS_USER_KEY)
  }
  /**
   * Retrieve and deserialize user from localStorage
   * @returns {Object} user object
   */
  static getUser() {
    const user = localStorage.getItem(AUTH_LS_USER_KEY)

    // No user stored
    if (!user) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('No user saved in localStorage')
      }
      return null
    }
    // Deserialize and return
    return JSON.parse(user)
  }
  /**
   * Persist user to localStorage
   * @param {Object} user user payload
   */
  static storeUser(user) {
    const serialized = JSON.stringify(user)
    localStorage.setItem(AUTH_LS_USER_KEY, serialized)
  }
  /**
   * Check if a user saved to localStorage
   * @returns {Boolean}
   */
  static userExists() {
    return localStorage.getItem(AUTH_LS_USER_KEY) !== null &&
      +(JSON.parse(localStorage.getItem(AUTH_LS_USER_KEY)).exp) > Date.now()
  }
}

// Export a singleton instance
export const loginSessionManager = new LoginSessionManager()



// WEBPACK FOOTER //
// ./src/store/modules/auth/auth.helpers.js